<template>
  <Simulation
    v-if="currentScreenSize < screens.lg"
    :price="product.price"
    :productCategoryValue="product.category"
    :offeredMonths="offeredMonths"
    class="block mb-4"
  />
  <VeeForm v-slot="{ handleSubmit }" :validation-schema="formSchema" as="div" ref="subscriptionForm">
    <form @submit="handleSubmit($event, submitForm)">
      <div class="sm:inline lg:flex">
        <div class="lg:w-4/5 lg:mr-4">
          <SellerForm :sellerCompleteName="sellerCompleteName" @selectedSeller="handleSelectedSeller" />
          <CategorySelector @selectProductCategory="handleSelectProductCategory" :categories="categories" />
          <ProductForm v-model="product" :isBikeCategory="isBikeCategory" />
          <component
            :is="getCustomerFormComponent"
            v-model="customer"
            :address2="address.address2"
            :addressError="address.addressError"
            @update:address2="address.address2 = $event"
            @newAddress="newAddress"
          />
        </div>
        <div class="lg:w-2/5 mt-4 lg:mt-0" v-if="currentScreenSize >= screens.lg">
          <div class="bg-white p-4 rounded-md mb-4">
            <div class="flex justify-between items-center">
              <p class="font-bold text-xl tracking-tight text-gray-900">Ma feuille de route</p>
              <button
                @click="$router.push('/pos/analytics/roadmap')"
                class="px-3 py-1.5 text-sm rounded-lg bg-primary-light hover:bg-primary transition-colors text-primary-dark hover:text-white flex items-center gap-2 group"
              >
                <span>Afficher</span>
                <ChartSquareBarIcon class="h-4 w-4 transform group-hover:scale-110 transition-transform" />
              </button>
            </div>
          </div>
          <Simulation :price="product.price" :productCategoryValue="product.category" :offeredMonths="offeredMonths" />
          <ContractDetailsSection :categories="categories" :productCategoryValue="product.category" />
          <FormButtons
            @sendTextMessage="handleSendTextMessage"
            @paymentInScreen="handlePaymentInScreen"
            @recordASale="handleRecordASale"
            :isLeadForm="isLeadForm"
          />
        </div>
      </div>
    </form>
  </VeeForm>
  <CustomerFormShortDetailsModal
    v-if="isLeadForm"
    :open="openCustomerFormShortDetailsModal"
    @closeCustomerFormShortDetailsModal="openCustomerFormShortDetailsModal = false"
    @submitCustomerFormShortDetailsModal="handleSubmitCustomerFormShortDetailsModal"
  />
  <SaleCreation
    :isOpen="isSaleCreationOpen"
    :saleRecording="saleRecording"
    :customer="customer"
    :product="product"
    :address="address"
    :sellerId="subscription.sellerId"
    @closeSaleModal="handleCloseSaleModal"
  />
  <OfferPayment
    :isOpen="isOfferPaymentOpen"
    :lead="subscription"
    :product="product"
    :customer="customer"
    @closePaymentModal="handleClosePaymentModal"
    @updateOffer="handleUpdateOffer"
  />
</template>

<script>
import { Form as VeeForm } from "vee-validate";
import { minimalFormSchema, basicFormSchema } from "@/components/pos/subscription/utils/validation";
import { mapActions, mapState, mapGetters } from "vuex";

import SellerForm from "@/components/pos/subscription/form/SellerForm.vue";
import ProductForm from "@/components/pos/subscription/form/ProductForm.vue";
import CustomerFormShort from "@/components/pos/subscription/form/CustomerFormShort.vue";
import CustomerFormLong from "@/components/pos/subscription/form/CustomerFormLong.vue";
import CustomerFormMedium from "@/components/pos/subscription/form/CustomerFormMedium.vue";
import CategorySelector from "@/components/selector/CategorySelector.vue";
import FormButtons from "@/components/pos/subscription/form/FormButtons.vue";
import Simulation from "@/components/pos/Simulation.vue";
import ContractDetailsSection from "@/components/pos/ContractDetailsSection.vue";
import CustomerFormShortDetailsModal from "@/components/pos/subscription/form/CustomerFormShortDetailsModal.vue";
import OfferPayment from "@/components/pos/subscription/payment/OfferPayment.vue";
import SaleCreation from "@/components/pos/subscription/salecreation/SaleCreation.vue";

import LeadService from "@/api/services/lead";
import SaleService from "@/api/services/sale";

import { EventBus } from "@/utils/event_bus";
import { screenSize, screens } from "@/utils/breakpoints.js";
import { toCamelCase } from "@estaly/ui/src/utils/data_formatter";
import { isPresent } from "@/utils/validation";
import _ from "lodash";
import { ChartSquareBarIcon } from "@heroicons/vue/outline";

export default {
  components: {
    VeeForm,
    SellerForm,
    CategorySelector,
    ProductForm,
    CustomerFormShort,
    CustomerFormLong,
    CustomerFormMedium,
    FormButtons,
    ContractDetailsSection,
    Simulation,
    CustomerFormShortDetailsModal,
    OfferPayment,
    SaleCreation,
    ChartSquareBarIcon,
  },
  props: {
    categories: {
      type: Array,
      required: true,
    },
    marketplace: {
      type: Object,
      required: true,
    },
    offeredMonths: {
      type: Array,
      required: true,
    },
  },

  data() {
    return {
      screens,
      subscription: {
        id: "",
        estalyId: "",
        uniqueToken: "",
        subscriptionLink: "",
        offers: [],
        discount: "",
        product: {
          title: "",
          price: "",
          referenceId: "",
          category: this.categories[0]?.value,
        },
        customer: {
          email: "",
          phone: "",
          firstName: "",
          lastName: "",
          birthDate: "",
          birthCity: "",
          birthCountry: "",
        },
        address: {
          address1: "",
          address2: "",
          city: "",
          province: "",
          country: "",
          zipCode: "",
          addressError: false,
        },
        invitationSentByEstaly: true,
        paymentInStorePlatform: false,
        sellerId: "",
      },
      openCustomerFormShortDetailsModal: false,
      recordASale: false,
      isSaleCreationOpen: false,
      isOfferPaymentOpen: false,
      saleRecording: {
        contractDetails: [],
        monthlyPrice: "",
      },
      sellerCompleteName: "",
    };
  },

  computed: {
    ...mapState("store", ["selectedStoreId"]),
    ...mapGetters("store", ["selectedStore"]),

    getCustomerFormComponent() {
      if (this.isLeadForm) {
        return "CustomerFormShort";
      } else if (this.isSaleWithoutBirthInfoForm) {
        return "CustomerFormMedium";
      }
      return "CustomerFormLong";
    },

    isLeadForm() {
      return this.selectedStore?.storeFeatureConfiguration?.subscriptionForm === "lead";
    },

    isSaleWithoutBirthInfoForm() {
      return this.selectedStore?.storeFeatureConfiguration?.subscriptionForm === "sale_without_birth_info";
    },

    formSchema() {
      return this.isLeadForm || this.isSaleWithoutBirthInfoForm ? minimalFormSchema : basicFormSchema;
    },

    product: {
      get() {
        return this.subscription.product;
      },
      set(value) {
        this.subscription.product = value;
      },
    },

    customer: {
      get() {
        return this.subscription.customer;
      },
      set(value) {
        this.subscription.customer = value;
      },
    },

    address: {
      get() {
        return this.subscription.address;
      },
      set(value) {
        this.subscription.address = value;
      },
    },

    isBikeCategory() {
      return this.product.category === "bike";
    },

    currentScreenSize() {
      return screenSize();
    },
  },

  methods: {
    ...mapActions("notifications", ["notify"]),
    ...mapActions("store", ["withLoader"]),

    handleSelectedSeller(seller) {
      this.sellerCompleteName = seller ? seller.name : "";
      this.subscription.sellerId = seller ? seller.id : "";
    },

    handleSelectProduct(product) {
      this.product.title = isPresent(product) && product.title.length > 0 ? product.title : "";
    },

    handleSelectProductCategory(selectedProductCategory) {
      this.product = {
        title: "",
        price: "",
        referenceId: "",
        category: selectedProductCategory?.value,
      };
    },

    handleRecordASale() {
      this.recordASale = true;
    },

    handleSendTextMessage() {
      this.subscription.invitationSentByEstaly = true;
    },

    handlePaymentInScreen() {
      this.subscription.invitationSentByEstaly = false;
      this.subscription.paymentInStorePlatform = true;
    },

    handleCloseSaleModal(resetAttributes) {
      if (resetAttributes) {
        this.resetAttributes();
      } else {
        this.isSaleCreationOpen = false;
      }
    },

    handleClosePaymentModal(resetAttributes) {
      if (resetAttributes) {
        this.resetAttributes();
      } else {
        this.isOfferPaymentOpen = false;
      }
    },

    handleUpdateOffer({ index, offer }) {
      this.subscription.offers = [
        ...this.subscription.offers.slice(0, index),
        offer,
        ...this.subscription.offers.slice(index + 1),
      ];
    },

    async submitForm() {
      if (this.isLeadForm) {
        if (this.recordASale) {
          this.openCustomerFormShortDetailsModal = true;
        } else {
          await this.handleLeadCreation();
        }
      } else {
        if (isPresent(this.address.address1) && isPresent(this.address.city) && isPresent(this.address.country)) {
          if (this.recordASale) {
            await this.handleSaleRecording();
          } else {
            await this.handleLeadCreation();
          }
        } else {
          this.address.addressError = true;
        }
      }
    },

    async handleLeadCreation() {
      const params = {
        lead: _.pick(this.subscription, [
          "customer",
          "address",
          "product",
          "invitationSentByEstaly",
          "paymentInStorePlatform",
          "sellerId",
        ]),
      };

      await this.withLoader(async () => {
        const response = await LeadService.create(this.selectedStoreId, params);
        if (response?.success) {
          const data = toCamelCase(response.data);
          if (this.subscription.paymentInStorePlatform) {
            Object.assign(this.subscription, {
              id: data.lead.id,
              estalyId: data.lead.estalyId,
              offers: data.lead.offers,
              discount: data.lead.discount,
            });
            if (this.isLeadForm) {
              this.openCustomerFormShortDetailsModal = true;
            } else {
              this.isOfferPaymentOpen = true;
            }
            this.subscription.paymentInStorePlatform = false;
          } else {
            this.notify({
              category: "simple",
              type: "success",
              title: this.isLeadForm
                ? "Le devis a été envoyé avec succès."
                : "Le lien de paiement a été envoyé avec succès.",
            });
            this.resetAttributes();
          }
        }
      });
    },

    async handleSubmitCustomerFormShortDetailsModal(params) {
      if (this.recordASale) {
        this.subscription.customer = {
          ...this.subscription.customer,
          ...params.lead.customer,
        };
        this.address = params.lead.address;
        this.openCustomerFormShortDetailsModal = false;
        await this.handleSaleRecording();
      } else {
        const response = await LeadService.update(this.subscription.estalyId, params);
        if (response?.success) {
          this.openCustomerFormShortDetailsModal = false;
          this.isOfferPaymentOpen = true;
        }
      }
    },

    async handleSaleRecording() {
      await this.withLoader(async () => {
        const response = await SaleService.seeContractDetails(
          this.product.category,
          this.product.price,
          this.selectedStoreId,
        );
        if (response?.success) {
          const data = toCamelCase(response.data);
          this.saleRecording.contractDetails = data.contracts;
          this.monthlyPrice = (parseFloat(this.saleRecording.contractDetails[0].price) / 12).toFixed(2);
        }
      });
      this.isSaleCreationOpen = true;
      this.recordASale = false;
    },

    newAddress(value) {
      this.address = value;
      if (isPresent(this.address.address1) && isPresent(this.address.city) && isPresent(this.address.country)) {
        this.address.addressError = false;
      }
    },

    resetAttributes() {
      this.subscription = {
        id: "",
        estalyId: "",
        uniqueToken: "",
        subscriptionLink: "",
        offers: [],
        discount: "",
        product: {
          title: "",
          price: "",
          referenceId: "",
          category: this.categories[0]?.value,
        },
        customer: {
          email: "",
          phone: "",
          firstName: "",
          lastName: "",
          birthDate: "",
          birthCity: "",
          birthCountry: "",
        },
        address: {
          address1: "",
          address2: "",
          city: "",
          province: "",
          country: "",
          zipCode: "",
          addressError: false,
        },
        invitationSentByEstaly: true,
        paymentInStorePlatform: false,
        sellerId: "",
      };
      this.saleRecording = {
        contractDetails: [],
        monthlyPrice: "",
      };

      this.isSaleCreationOpen = false;
      this.openCustomerFormShortDetailsModal = false;
      this.isOfferPaymentOpen = false;
      this.recordASale = false;
      this.sellerCompleteName = "";

      EventBus.emit("resetAddress");
      EventBus.emit("resetSelectedProductCategory");
      this.$refs.subscriptionForm.resetForm();
    },
  },
};
</script>
